import React from 'react';
import PropTypes from 'prop-types';
import {
  disclosureInfoUri, electronicMessageDisclosure, facetAccountLinkingFAQ,
} from 'Consts/clientUris';
import { makeStyles } from '@material-ui/core/styles';
import classNames from 'classnames';
import Icon from 'Components/UI/Icon';
import useMobileLayout from 'Hooks/useMobileLayout';
import { useLayoutContext } from 'Context/LayoutContext';
import { formatPhoneNumber } from 'Helpers/Formats';
import {
  facetCity,
  facetState,
  facetStreetAddress,
  facetZip,
  facetPhoneMain,
  FACET_MY_EXPERIENCE_EMAIL_ADDRESS,
} from 'Consts/Strings';
import Text from 'Components/ComponentLibrary/Atoms/Text';
import { getBorder } from 'Components/utils';
import fr from 'facet-rest';
import { navigateToPage } from 'Views/Page/Content/Templates';
import useUserContext from 'Context/UserContext';
import { FDContentContainer } from '@facet-wealth/design';

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    transform: 'translateY(100%)',
    position: 'absolute',
    bottom: 0,
  },
  footerHidden: {
    display: 'none',
  },
  mainFooter: {
    ...getBorder(theme, { top: true }),
    display: 'flex',
    flexDirection: 'column',
    fontSize: '12px',
    lineheight: '16px',
    fontFamily: 'Euclid Square',
    fontWeight: 300,
    color: theme.palette.facet.primaryGray,
  },
  footerContainer: {
    width: '100%',
    margin: '0 auto',
  },
  mainFooterContent: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: theme.spacing(3, 0),
  },
  footerColumn: {
    flexGrow: 1,
  },
  flexRow: {
    display: 'flex',
    flexDirection: 'row',
  },
  flexCol: {
    display: 'flex',
    flexDirection: 'column',
  },
  colChildren: {
    padding: theme.spacing(2, 0),
    '& div': {
      marginBottom: theme.spacing(0.5),
    },
  },
  copyright: {
    color: theme.palette.facet.primaryGray,
    paddingBottom: theme.spacing(4),
  },
  centered: {
    justifyContent: 'center',
    textAlign: 'center',
  },
  linkList: {
    marginTop: theme.spacing(2),
  },
  footerLink: {
    display: 'block',
    marginBottom: theme.spacing(0.5),
    color: theme.palette.facet.primaryBlue,
    '&:hover': {
      cursor: 'pointer',
      textDecoration: 'underline',
    },
  },
}));

const LinkList = ({ links }) => {
  const classes = useStyles();
  return (
    <nav className={classes.linkList}>
      {
        links.filter(v => !v.hidden).map((link) => {
          const listLink = (
            <a
              key={link.text}
              className={classNames({
                [classes.footerLink]: !link.label,
              })}
              target={link.openInNewTab && '_blank'}
              rel={link.openInNewTab && 'noopener noreferrer'}
              href={link.url}
              onClick={link.onClick}
            >
              {link.text}
            </a>
          );
          return link.label
            ? <p key={link.text} className={classes.footerLink}>{link.label} {listLink}</p>
            : listLink;
        })
      }
    </nav>
  );
};

const FooterColumn = ({
  title, links, children, isMobile, icon,
}) => {
  const classes = useStyles();
  return (
    <div className={classes.footerColumn}>
      <div
        className={classNames(classes.flexRow, {
          [classes.centered]: isMobile,
        })}
      >
        {icon && <Icon src={icon} height="25px" width="25px" />}
        <Text
          variant="h4"
          color="black"
          weight={'bold'}
        >
          {title}
        </Text>
      </div>
      <div
        className={classes.flexCol}
      >
        <LinkList links={links} />
        {children && children}
      </div>
    </div>
  );
};

const Footer = ({ className, plannerDetail, householdResource }) => {
  const classes = useStyles();
  const isMobile = useMobileLayout();
  const { isFooterVisible } = useLayoutContext();
  const { isAgent } = useUserContext();

  const plannerName = plannerDetail && `${plannerDetail.attr('firstName')} ${plannerDetail.attr('lastName')}`;
  const plannerEmail = plannerDetail && plannerDetail.attr('email');
  const generalFAQPageSelfLink = householdResource?.relationship(fr.relationships.generalFAQ.name)?.link('related').url();
  const taxFormsFAQPageSelfLink = householdResource?.relationship(fr.relationships.taxFormsFAQ.name)?.link('related').url();

  // ----- FOOTER COLUMN SCHEMA -----
  // title (string) - Header of column
  // icon (URI) - Icon URI
  // links (array) - Links to display in column
  //  |- text (string) - Text of link
  //  |- url (string) - URL of link
  //  |- hidden (boolean) - Bool to hide link
  //  |- openInNewTab (boolean) - open link in new tab
  // children (element / string) - Displays a new column block within itself
  const footerColumns = [
    {
      title: 'Support',
      links: [
        {
          text: 'Frequently Asked Questions',
          openInNewTab: true,
          onClick: () => {
            navigateToPage(generalFAQPageSelfLink, isAgent);
          },
          hidden: !householdResource?.relationship(fr.relationships.generalFAQ.name),
        },
        {
          text: 'Account Linking FAQs',
          url: facetAccountLinkingFAQ,
          openInNewTab: true,
        },
        {
          text: 'Tax FAQs',
          openInNewTab: true,
          onClick: () => {
            navigateToPage(taxFormsFAQPageSelfLink, isAgent);
          },
          hidden: !householdResource?.relationship(fr.relationships.taxFormsFAQ.name),
        },
        {
          text: `Contact ${plannerName}`,
          url: `mailto:${plannerEmail}`,
          hidden: !plannerDetail,
        },
      ],
    },
    {
      title: 'Disclosure',
      links: [
        {
          text: 'Important Disclosure Information.',
          url: disclosureInfoUri,
          openInNewTab: true,
        },
        {
          text: 'Electronic Message Disclosure',
          url: electronicMessageDisclosure,
          openInNewTab: true,
        },
      ],
    },
    {
      title: 'Contact',
      links: [
        {
          label: 'Main Line -',
          text: formatPhoneNumber(facetPhoneMain),
          url: `tel:${facetPhoneMain}`,
        },
        {
          text: FACET_MY_EXPERIENCE_EMAIL_ADDRESS,
          url: `mailto:${FACET_MY_EXPERIENCE_EMAIL_ADDRESS}`,
        },
      ],
      children: (
        <>
          <div>{facetStreetAddress}</div>
          <div>{`${facetCity}, ${facetState} ${facetZip}`}</div>
        </>
      ),
    },
  ];

  return (
    <section
      id="footer"
      className={classNames(className, classes.root, {
        [classes.footerHidden]: !isFooterVisible,
      })}
    >
      <div className={classes.mainFooter}>
        <FDContentContainer maxWidth="xl">
          <div
            className={classNames({
              [classes.mainFooterContent]: !isMobile,
              [classes.flexCol]: isMobile,
              [classes.centered]: isMobile,
            })}
          >
            {footerColumns.map(col => (col.children
              ? (
                <FooterColumn key={col.title} title={col.title} links={col.links} icon={col.icon} isMobile={isMobile}>
                  <div className={classes.colChildren}>{col.children}</div>
                </FooterColumn>
              )
              : <FooterColumn key={col.title} title={col.title} links={col.links} icon={col.icon} isMobile={isMobile} />))
          }
          </div>
        </FDContentContainer>
      </div>
      <div
        className={classNames(classes.copyright, {
          [classes.centered]: isMobile,
        })}
      >
        <FDContentContainer maxWidth="xl">
          <div className={classes.footerColumn}>
            {`Copyright © ${new Date().getFullYear()} Facet. All rights reserved.`}
          </div>
        </FDContentContainer>
      </div>
    </section>
  );
};

Footer.defaultProps = {
  plannerDetail: null,
  householdResource: null,
  className: '',
};

FooterColumn.defaultProps = {
  icon: null,
  children: null,
};

Footer.propTypes = {
  plannerDetail: PropTypes.object,
  householdResource: PropTypes.object,
  className: PropTypes.string,
};

LinkList.propTypes = {
  links: PropTypes.array.isRequired,
};

FooterColumn.propTypes = {
  title: PropTypes.string.isRequired,
  icon: PropTypes.string,
  links: PropTypes.array.isRequired,
  children: PropTypes.element,
  isMobile: PropTypes.bool.isRequired,
};

export default Footer;
